// LDT-710 (épica LDT-698, absorbe LDT-224) · El aviso de privacidad que cada
// parque da a sus familias, generado por Playium con los datos del parque.
//
// El parque es el responsable de los datos de sus familias y tiene que
// informarles (arts. 13-14 RGPD; art. 12 LQPD). Hasta ahora solo tenía un texto
// libre (`settings.legal_notice`) que muchos no rellenaban. Playium genera el
// aviso con una plantilla (`src/locales/venuePrivacy/{es,ca,en}.json`) y los
// datos del parque, y lo publica en `/app/:slug/privacidad`.
//
// ESTE MÓDULO decide, sin ninguna dependencia (lo cargan el navegador y Deno,
// como `billing/venue-legal-docs.ts`):
//
//   · si el aviso se publica: solo con razón social, NRT/NIF, dirección y
//     teléfono rellenos (sin contar espacios). Con alguno vacío NO se publica
//     (nada de plantillas con «EMPRESA A», el fallo de CoverManager) y las
//     familias siguen viendo lo de siempre: la política de privacidad de
//     Playium y el aviso legal del parque (LDT-706).
//   · qué secciones lleva: las que activa la configuración del parque (app de
//     familias, calendario público, pagos, WhatsApp, feed ICS, reservas de
//     mesa). Solo se cuenta lo que el parque hace de verdad.
//   · su VERSIÓN: el sha256 de un JSON canónico con los datos del parque, las
//     secciones activas y la versión de la plantilla. Es la «versión» que se
//     acepta (`documents.venue_privacy_notice`) y el JSON se guarda en
//     `venue_legal_notice_versions` como prueba de qué se mostró. Cambia si el
//     parque cambia sus datos o su configuración, o si cambia la plantilla:
//     entonces las familias vuelven a aceptar.
//
// El texto adicional del parque (`settings.legal_notice`) NO entra en el hash:
// sigue siendo su propio documento (`venue_legal_notice`, LDT-706), que el
// aviso enseña como «Información adicional del parque».
//
// Cliente y servidor usan ESTE mismo fichero (el cliente lo reexporta desde
// `src/lib/venue-privacy-notice.ts`): no hay dos copias que puedan divergir.

/**
 * Versión de la plantilla del aviso (`src/locales/venuePrivacy/*.json`,
 * clave `notice`). Se sube cuando cambia lo que dice el texto:
 * `src/test/aviso-privacidad-parque.test.ts` guarda el hash de la plantilla de
 * cada versión y falla si el texto cambia sin subirla.
 */
// LDT-711: 2026-09-30 — la sección «Alergias y necesidades especiales»
// (`healthNotes`) y la de «Datos de salud» ya no dice que Playium no tiene
// campos de salud. No puede seguir siendo 2026-09-29: esa versión, con el texto
// anterior, ya la han aceptado familias en staging.
export const FAMILY_NOTICE_TEMPLATE_VERSION = "2026-09-30";

/** Clave del aviso del parque dentro de `legal_acceptances.documents`. */
export const VENUE_PRIVACY_DOC = "venue_privacy_notice";

/** Los datos sin los que el aviso no se publica, en el orden en que se piden. */
export const NOTICE_REQUIRED_FIELDS = ["legal_name", "nrt", "address1", "phone"] as const;
export type NoticeRequiredField = typeof NOTICE_REQUIRED_FIELDS[number];

/**
 * Secciones condicionales del aviso. `icsFeed` e `icsFeedMinimal` se excluyen:
 * el feed del calendario externo va completo o reducido (LDT-703).
 */
export const NOTICE_SECTIONS = [
  "familyApp",
  "publicCalendar",
  "payments",
  "whatsapp",
  "icsFeed",
  "icsFeedMinimal",
  "tableReservations",
  // LDT-711 · Alergias y necesidades especiales por reserva o inscripción
  // (`settings.health_notes_enabled`): el dato de salud, con consentimiento.
  "healthNotes",
] as const;
export type NoticeSection = typeof NOTICE_SECTIONS[number];

/** Las columnas de `settings` que lee el aviso (para los `.select()`). */
export const VENUE_PRIVACY_SETTINGS_COLUMNS = [
  "legal_name",
  "nrt",
  "address1",
  "address2",
  "phone",
  "module_online_management",
  "mobile_enabled",
  "module_public_calendar",
  "module_payments",
  "module_whatsapp_bot",
  "calendar_feed_token",
  "calendar_feed_minimal",
  "module_table_reservations",
  "health_notes_enabled",
] as const;

/** `select` listo para usar: las columnas de arriba separadas por comas. */
export const VENUE_PRIVACY_SETTINGS_SELECT = VENUE_PRIVACY_SETTINGS_COLUMNS.join(", ");

/** La fila de `settings` con lo que lee el aviso (todo opcional: puede faltar). */
export interface NoticeSettings {
  legal_name?: string | null;
  nrt?: string | null;
  address1?: string | null;
  address2?: string | null;
  phone?: string | null;
  module_online_management?: boolean | null;
  mobile_enabled?: boolean | null;
  module_public_calendar?: boolean | null;
  module_payments?: boolean | null;
  module_whatsapp_bot?: boolean | null;
  calendar_feed_token?: string | null;
  calendar_feed_minimal?: boolean | null;
  module_table_reservations?: boolean | null;
  health_notes_enabled?: boolean | null;
}

/** Los datos del parque que salen en el aviso. */
export interface NoticeVenue {
  legalName: string;
  nrt: string;
  address1: string;
  /** Vacío si el parque no tiene segunda línea. */
  address2: string;
  phone: string;
  /** `locals.country` (ISO alpha-2, en mayúsculas). Decide la autoridad de control. */
  country: string;
}

/** Todo lo que determina el texto del aviso: lo que se versiona. */
export interface VenuePrivacyNoticeInputs {
  template: string;
  venue: NoticeVenue;
  /** Ordenadas: el mismo conjunto da siempre el mismo JSON. */
  sections: NoticeSection[];
}

export type VenuePrivacyNoticeState =
  | { kind: "published"; inputs: VenuePrivacyNoticeInputs }
  | { kind: "incomplete"; missing: NoticeRequiredField[] };

const text = (v: unknown): string => (typeof v === "string" ? v.trim() : "");

/** Qué secciones condicionales lleva el aviso con esta configuración. */
export function noticeSections(s: NoticeSettings | null | undefined): NoticeSection[] {
  const out: NoticeSection[] = [];
  // La app de familias la gobiernan estas dos columnas (lo mismo que miran
  // `decideParentAppPreLogin`, `mobile-api` y el catálogo del bot).
  const familyApp = !!s?.module_online_management || !!s?.mobile_enabled;
  if (familyApp) out.push("familyApp");
  if (s?.module_public_calendar) out.push("publicCalendar");
  if (s?.module_payments) out.push("payments");
  if (s?.module_whatsapp_bot) out.push("whatsapp");
  // El feed existe mientras haya token (`calendar-feed` lo busca por él).
  if (text(s?.calendar_feed_token)) out.push(s?.calendar_feed_minimal === true ? "icsFeedMinimal" : "icsFeed");
  // Las reservas de mesa nacen en la app de familias: sin ella no hay dónde.
  if (s?.module_table_reservations && familyApp) out.push("tableReservations");
  // LDT-711: encenderla cambia la versión del aviso y las familias vuelven a
  // aceptarlo, esta vez sabiendo que el parque puede pedir el dato de salud.
  if (s?.health_notes_enabled === true) out.push("healthNotes");
  return out.sort();
}

/**
 * ¿Se publica el aviso de este parque? Con los cuatro datos obligatorios, sí
 * (y solo entonces): se publica solo, sin interruptor (decisión de David,
 * pregunta 2 del plan, opción A).
 */
export function venuePrivacyNoticeState(
  settings: NoticeSettings | null | undefined,
  country: string | null | undefined,
): VenuePrivacyNoticeState {
  const s = settings ?? {};
  const missing = NOTICE_REQUIRED_FIELDS.filter((f) => !text(s[f]));
  if (missing.length > 0) return { kind: "incomplete", missing };
  return {
    kind: "published",
    inputs: {
      template: FAMILY_NOTICE_TEMPLATE_VERSION,
      venue: {
        legalName: text(s.legal_name),
        nrt: text(s.nrt),
        address1: text(s.address1),
        address2: text(s.address2),
        phone: text(s.phone),
        country: text(country).toUpperCase(),
      },
      sections: noticeSections(s),
    },
  };
}

/** Atajo: las entradas del aviso publicado, o null si no se publica. */
export function publishedNoticeInputs(
  settings: NoticeSettings | null | undefined,
  country: string | null | undefined,
): VenuePrivacyNoticeInputs | null {
  const st = venuePrivacyNoticeState(settings, country);
  return st.kind === "published" ? st.inputs : null;
}

/**
 * El JSON canónico: claves en orden fijo, secciones ordenadas y sin repetir.
 * Es lo que se hashea y lo que se guarda en `venue_legal_notice_versions.body`.
 */
export function canonicalNoticeJson(inputs: VenuePrivacyNoticeInputs): string {
  const v = inputs.venue;
  return JSON.stringify({
    template: inputs.template,
    venue: {
      legalName: v.legalName,
      nrt: v.nrt,
      address1: v.address1,
      address2: v.address2,
      phone: v.phone,
      country: v.country,
    },
    sections: [...new Set(inputs.sections)].sort(),
  });
}

async function sha256HexOf(textIn: string): Promise<string> {
  const digest = new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(textIn)));
  return Array.from(digest).map((b) => b.toString(16).padStart(2, "0")).join("");
}

/** La versión del aviso: sha256 (hex) del JSON canónico. */
export function venuePrivacyNoticeVersion(inputs: VenuePrivacyNoticeInputs): Promise<string> {
  return sha256HexOf(canonicalNoticeJson(inputs));
}

/**
 * Lee unas entradas que llegan por la red (la respuesta de `public-calendar`,
 * el cuerpo de un 409). Devuelve null si no tienen la forma esperada: entonces
 * el cliente trata el aviso como no publicado y el servidor, si sí lo está, lo
 * rechaza con 409 y manda el vigente.
 */
export function parseNoticeInputs(raw: unknown): VenuePrivacyNoticeInputs | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  const v = r.venue as Record<string, unknown> | undefined;
  if (typeof r.template !== "string" || !v || typeof v !== "object") return null;
  const fields = ["legalName", "nrt", "address1", "address2", "phone", "country"] as const;
  if (!fields.every((f) => typeof v[f] === "string")) return null;
  if (!Array.isArray(r.sections) || !r.sections.every((x) => (NOTICE_SECTIONS as readonly unknown[]).includes(x))) return null;
  return {
    template: r.template,
    venue: {
      legalName: v.legalName as string,
      nrt: v.nrt as string,
      address1: v.address1 as string,
      address2: v.address2 as string,
      phone: v.phone as string,
      country: v.country as string,
    },
    sections: [...new Set(r.sections as NoticeSection[])].sort(),
  };
}

/** Autoridad de control según el país del parque. */
export type SupervisoryAuthority = "apda" | "aepd" | "other";

export function supervisoryAuthority(country: string | null | undefined): SupervisoryAuthority {
  const c = text(country).toUpperCase();
  if (c === "AD") return "apda";
  if (c === "ES") return "aepd";
  return "other";
}

/** Ruta pública del aviso (bajo `/app`, que Vercel sirve con app.html). */
export function venuePrivacyNoticePath(slug: string): string {
  return `/app/${encodeURIComponent(slug)}/privacidad`;
}
